import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Team } from '@/redux/types/team'
import { RoomMember } from '@/redux/types/room'
import { SERVER_URL } from '@/api/config'

interface TeamOrientationInfoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAcknowledge: () => void
  team: Team | null
  members: RoomMember[]
  currentUserEmail?: string
}

export default function TeamOrientationInfoDialog({
  open,
  onOpenChange,
  onAcknowledge,
  team,
  members,
  currentUserEmail,
}: TeamOrientationInfoDialogProps) {
  const teamName = team?.name || 'Your Team'
  const teamDescription =
    team?.description?.trim() ||
    'No team description has been set yet. Ask your professor for more context.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[620px] max-h-[85vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='text-lg font-bold text-[#0F172A]'>
            Team Orientation
          </DialogTitle>
          <DialogDescription className='text-[#64748B]'>
            Review your team details before the game starts.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <section className='rounded-md border border-slate-200 bg-slate-50 p-3'>
            <p className='text-xs uppercase tracking-wide text-slate-500 font-semibold'>
              Team Name
            </p>
            <p className='mt-1 text-base font-semibold text-slate-900'>
              {teamName}
            </p>
          </section>

          <section className='rounded-md border border-slate-200 bg-white p-3'>
            <p className='text-xs uppercase tracking-wide text-slate-500 font-semibold'>
              Team Members ({members.length})
            </p>
            {members.length === 0 ? (
              <p className='mt-2 text-sm text-slate-600'>
                No accepted team members found yet.
              </p>
            ) : (
              <ul className='mt-2 space-y-2'>
                {members.map((member) => {
                  const isCurrentUser = member.member_email === currentUserEmail
                  const initials =
                    member.member_name
                      ?.split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2) || '?'
                  const avatarUrl = member.member_profile_picture
                    ? `${SERVER_URL}${member.member_profile_picture}`
                    : undefined

                  return (
                    <li
                      key={member.member_id ?? member.member_email}
                      className='flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50 px-2 py-2'
                    >
                      <Avatar className='h-8 w-8'>
                        <AvatarImage src={avatarUrl} alt={member.member_name} />
                        <AvatarFallback className='text-xs'>
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className='min-w-0'>
                        <p className='text-sm font-medium text-slate-900 truncate'>
                          {member.member_name || member.member_email}
                          {isCurrentUser ? ' (you)' : ''}
                        </p>
                        <p className='text-xs text-slate-500 truncate'>
                          {member.member_email}
                        </p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section className='rounded-md border border-slate-200 bg-white p-3'>
            <p className='text-xs uppercase tracking-wide text-slate-500 font-semibold'>
              Team Description
            </p>
            <p className='mt-2 text-sm text-slate-800 whitespace-pre-wrap'>
              {teamDescription}
            </p>
          </section>
        </div>

        <div className='flex justify-end'>
          <Button
            type='button'
            className='bg-dark-blue hover:bg-dark-blue-hovered'
            onClick={onAcknowledge}
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
